import 'react-bootstrap-typeahead'
import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Typeahead } from 'react-bootstrap-typeahead'
import { JournalBookmark, Paperclip } from 'react-bootstrap-icons'
import { LuGraduationCap } from 'react-icons/lu'
import axios from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import { getFilteredSubjects } from './admitCardHelpers'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import MediaUploadField from './commonComponents/MediaUploadField'
import {
  createHomeworkRequest,
  fetchAcademicSessionIdForInstitute,
  fetchHomeworkById,
  getHomeworkErrorMessage,
  parseHomeworkIdFromResponse,
  parseHomeworkOne,
  resolveInstituteId,
  updateHomeworkRequest,
  uploadHomeworkMedia,
} from './commonComponents/homeworkApi'
import { useInstitute } from './commonComponents/InstituteContext'

const resolveClassId = (cls) => cls?.class_id ?? cls?.classId ?? cls?.id

const resolveCourseId = (cls) =>
  cls?.course_id ?? cls?.courseId ?? cls?.course?.id ?? ''

const getRequestHeaders = () => {
  let token = null
  let instituteId = Cookies.get('institute_id')
  const userCookie = Cookies.get('user')
  if (userCookie) {
    try {
      const parsed = JSON.parse(userCookie)
      token = parsed.token || null
      if (!instituteId && parsed.institute_id != null && parsed.institute_id !== '') {
        instituteId = String(parsed.institute_id)
      }
    } catch {
      /* ignore */
    }
  }
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  const instNum = Number(instituteId)
  if (Number.isFinite(instNum) && instNum > 0) {
    headers['institute-Id'] = instNum
  }
  return { token, headers }
}

const normalizeClassOption = (cls) => {
  const classId =
    cls?.class_id ?? cls?.classId ?? cls?.class?.id ?? cls?.id
  const courseId =
    cls?.course_id ??
    cls?.courseId ??
    cls?.course?.id ??
    (typeof cls?.course === 'number' ? cls.course : '')
  const className =
    cls?.class_name ??
    cls?.className ??
    cls?.name ??
    cls?.class?.class_name ??
    ''
  const courseName =
    typeof cls?.course === 'string'
      ? cls.course
      : cls?.course_name ??
        cls?.courseName ??
        cls?.course?.name ??
        ''
  return {
    ...cls,
    class_id: classId,
    class_name: className,
    course_id: courseId,
    course_name: courseName,
  }
}

const parseAssignedClasses = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.map(normalizeClassOption).filter((c) => c.class_name && resolveClassId(c))
  }
  const list = raw.assigned_classes ?? raw.assignedClasses ?? raw.classes ?? []
  if (!Array.isArray(list)) return []
  return list.map(normalizeClassOption).filter((c) => c.class_name && resolveClassId(c))
}

const getEffectiveCourseId = (classRecord, courses) => {
  if (!classRecord) return ''
  const direct = resolveCourseId(classRecord)
  if (direct) return String(direct)
  const name = classRecord.course_name || classRecord.course
  if (!name || !Array.isArray(courses)) return ''
  const match = courses.find(
    (c) => String(c?.name || '').toLowerCase() === String(name).toLowerCase(),
  )
  return match?.id != null ? String(match.id) : ''
}

const getClassCourseName = (classRecord, courseList) => {
  if (!classRecord) return ''
  const direct =
    classRecord.course_name ||
    (typeof classRecord.course === 'string' ? classRecord.course : '')
  if (direct) return direct
  const courseId = getEffectiveCourseId(classRecord, courseList)
  if (!courseId || !Array.isArray(courseList)) return ''
  const match = courseList.find((c) => String(c.id) === String(courseId))
  return match?.name || ''
}

const formatClassWithCourse = (classRecord, courseList) => {
  const className = classRecord?.class_name || ''
  const courseName = getClassCourseName(classRecord, courseList)
  return courseName ? `${className} (${courseName})` : className
}

const MAX_FILE_BYTES = 5 * 1024 * 1024
const ALLOWED_EXT = ['.jpeg', '.jpg', '.png', '.pdf']

const validateFile = (file) => {
  if (!file) return null
  const name = file.name.toLowerCase()
  const ok = ALLOWED_EXT.some((ext) => name.endsWith(ext))
  if (!ok) return 'Only JPEG, JPG, PNG, or PDF files are allowed.'
  if (file.size > MAX_FILE_BYTES) return 'File must be 5MB or smaller.'
  return null
}

const readFileMeta = (file) => {
  if (!file) return { label: '', previewUrl: '' }
  const previewUrl = /\.(jpe?g|png)$/i.test(file.name)
    ? URL.createObjectURL(file)
    : ''
  return { label: file.name, previewUrl }
}

const normalizeDateInput = (value) => {
  if (!value) return ''
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

const attachmentLabelFromUrl = (url) => {
  if (!url) return ''
  try {
    return url.split('?')[0].split('/').pop() || 'Attachment'
  } catch {
    return 'Attachment'
  }
}

const attachmentPreviewFromUrl = (url) => {
  if (!url) return ''
  return /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url) ? url : ''
}

export default function HomeworkForm() {
  const { id: editId } = useParams()
  const isEditMode = Boolean(editId)
  const { logout, user, token } = useAuth()
  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const normalizedRoles = (user?.roles || []).map((role) => String(role).toLowerCase())
  const isTeacherUser =
    normalizedUserType === 'teacher' || normalizedRoles.includes('teacher')
  const isAdminUser =
    normalizedUserType === 'admin' || normalizedRoles.includes('admin')
  const canAccessHomework = isTeacherUser || isAdminUser

  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()
  const [submitting, setSubmitting] = useState(false)
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState('')

  const [form, setForm] = useState({
    title: '',
    due_date: '',
    description: '',
    status: 'published',
  })
  const [assignedClasses, setAssignedClasses] = useState([])
  const [subjects, setSubjects] = useState([])
  const [courses, setCourses] = useState([])
  const [classSubjects, setClassSubjects] = useState([])
  const [filtersLoading, setFiltersLoading] = useState(false)
  const [selectedClassId, setSelectedClassId] = useState('')
  const [selectedSubjectId, setSelectedSubjectId] = useState('')
  const [mediaFile, setMediaFile] = useState(null)
  const [mediaMeta, setMediaMeta] = useState({ label: '', previewUrl: '' })
  const [academicSessionId, setAcademicSessionId] = useState(null)

  useEffect(() => {
    const instituteId = resolveInstituteId()
    if (!instituteId) return
    let cancelled = false
    fetchAcademicSessionIdForInstitute(instituteId).then((id) => {
      if (!cancelled && id) setAcademicSessionId(id)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadFilterData = async () => {
      const { token: authToken, headers } = getRequestHeaders()
      if (!authToken) return

      setFiltersLoading(true)
      try {
        const [classRes, subjectRes, courseRes] = await Promise.all([
          axios.get(`${BASE_URL}/teacher-classes`, { headers }),
          ApiEndpoints.getAllSubjects(),
          ApiEndpoints.getAllCourses(),
        ])
        if (cancelled) return

        const classPayload = classRes.data?.data ?? classRes.data
        let parsed = parseAssignedClasses(classPayload)

        if (parsed.length === 0) {
          const teacherId = user?.id ?? user?.teacher_id
          if (teacherId) {
            try {
              const altRes = await axios.get(
                `${BASE_URL}/teacher-classes/${teacherId}`,
                { headers },
              )
              parsed = parseAssignedClasses(altRes.data?.data ?? altRes.data)
            } catch {
              /* use empty list */
            }
          }
        }

        setAssignedClasses(parsed)

        const subjectList = subjectRes?.data?.data ?? subjectRes?.data ?? []
        setSubjects(Array.isArray(subjectList) ? subjectList : [])

        const courseList = courseRes?.data?.data ?? courseRes?.data ?? []
        setCourses(Array.isArray(courseList) ? courseList : [])
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.message || 'Failed to load class and subject filters',
          )
          setAssignedClasses([])
        }
      } finally {
        if (!cancelled) setFiltersLoading(false)
      }
    }

    loadFilterData()
    return () => {
      cancelled = true
    }
  }, [user, token])

  useEffect(() => {
    if (!isEditMode || !editId) return

    let cancelled = false
    const loadHomework = async () => {
      setLoadingEdit(true)
      try {
        const res = await fetchHomeworkById(editId)
        if (cancelled) return
        const row = parseHomeworkOne(res)
        if (!row) {
          toast.error('Homework not found.')
          navigate('/homework', { replace: true })
          return
        }

        const classId = row.class_id != null ? String(row.class_id) : ''
        setForm({
          title: row.title || '',
          due_date: normalizeDateInput(row.due_date),
          description: row.description || '',
          status: row.status || 'published',
        })
        setSelectedClassId(classId)
        setSelectedSubjectId(
          row.subject_id != null && row.subject_id !== '' ? String(row.subject_id) : '',
        )

        const sessionFromRow = Number(row.academic_session_id)
        if (Number.isFinite(sessionFromRow) && sessionFromRow > 0) {
          setAcademicSessionId(sessionFromRow)
        }

        const attachmentUrl = row.attachment_url || ''
        setExistingAttachmentUrl(attachmentUrl)
        if (attachmentUrl) {
          setMediaMeta({
            label: attachmentLabelFromUrl(attachmentUrl),
            previewUrl: attachmentPreviewFromUrl(attachmentUrl),
          })
        }

        if (classId) {
          setAssignedClasses((prev) => {
            if (prev.some((c) => String(resolveClassId(c)) === classId)) return prev
            return [
              ...prev,
              normalizeClassOption({
                class_id: row.class_id,
                class_name: row.class_name,
                course_id: row.course_id,
                course_name: row.course_name,
              }),
            ]
          })
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(getHomeworkErrorMessage(err))
          navigate('/homework', { replace: true })
        }
      } finally {
        if (!cancelled) setLoadingEdit(false)
      }
    }

    loadHomework()
    return () => {
      cancelled = true
    }
  }, [isEditMode, editId, navigate])

  const selectedClassRecord = useMemo(
    () =>
      assignedClasses.find(
        (cls) => String(resolveClassId(cls)) === String(selectedClassId),
      ) || null,
    [assignedClasses, selectedClassId],
  )

  useEffect(() => {
    if (!selectedClassId || !selectedClassRecord) {
      setClassSubjects([])
      return
    }

    let cancelled = false
    const courseId = getEffectiveCourseId(selectedClassRecord, courses)

    const loadClassSubjects = async () => {
      const { headers } = getRequestHeaders()
      if (courseId) {
        try {
          const res = await ApiEndpoints.getSubjectsByCourse(courseId, { headers })
          if (cancelled) return
          const byCourse = res?.data?.data ?? []
          if (Array.isArray(byCourse) && byCourse.length > 0) {
            setClassSubjects(byCourse)
            return
          }
        } catch {
          /* fall through */
        }
      }

      if (cancelled) return

      const filtered = getFilteredSubjects(subjects, courseId, selectedClassId)
      if (filtered.length > 0) {
        setClassSubjects(filtered)
        return
      }

      if (courseId) {
        setClassSubjects(
          subjects.filter((s) => String(s?.course_id) === String(courseId)),
        )
      } else {
        setClassSubjects([])
      }
    }

    loadClassSubjects()
    return () => {
      cancelled = true
    }
  }, [selectedClassId, selectedClassRecord, courses, subjects])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const handleMediaFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const err = validateFile(file)
    if (err) {
      toast.error(err)
      e.target.value = ''
      return
    }
    if (mediaMeta.previewUrl) URL.revokeObjectURL(mediaMeta.previewUrl)
    setMediaFile(file)
    setMediaMeta(readFileMeta(file))
  }

  const clearMedia = () => {
    if (mediaMeta.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(mediaMeta.previewUrl)
    }
    setMediaFile(null)
    if (isEditMode && existingAttachmentUrl) {
      setMediaMeta({
        label: attachmentLabelFromUrl(existingAttachmentUrl),
        previewUrl: attachmentPreviewFromUrl(existingAttachmentUrl),
      })
    } else {
      setMediaMeta({ label: '', previewUrl: '' })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) {
      toast.error('Title is required.')
      return
    }
    if (!selectedClassId) {
      toast.error('Please select a class.')
      return
    }
    if (!form.due_date) {
      toast.error('Due date is required.')
      return
    }

    const instituteId = resolveInstituteId()
    if (!instituteId) {
      toast.error('Institute not selected. Please select an institute and try again.')
      return
    }

    const classId = Number(
      resolveClassId(selectedClassRecord) || selectedClassId,
    )
    if (!Number.isFinite(classId) || classId <= 0) {
      toast.error('Invalid class selected.')
      return
    }

    setSubmitting(true)
    try {
      let sessionId = academicSessionId
      if (!sessionId) {
        sessionId = await fetchAcademicSessionIdForInstitute(instituteId)
        if (sessionId) setAcademicSessionId(sessionId)
      }

      const description = form.description.trim()
      const subjectId = Number(selectedSubjectId)

      if (isEditMode) {
        const updatePayload = {
          class_id: classId,
          title: form.title.trim(),
          due_date: form.due_date,
          status: form.status || 'published',
          description: description || null,
          subject_id:
            Number.isFinite(subjectId) && subjectId > 0 ? subjectId : null,
        }
        if (sessionId) updatePayload.academic_session_id = sessionId

        const res = await updateHomeworkRequest(editId, updatePayload)

        if (mediaFile) {
          try {
            const uploadResult = await uploadHomeworkMedia(editId, mediaFile)
            toast.success(
              uploadResult.message ||
                res?.data?.message ||
                'Homework updated with new attachment.',
            )
          } catch (uploadErr) {
            toast.error(
              `${getHomeworkErrorMessage(uploadErr)} Homework was updated without the new attachment.`,
            )
            navigate('/homework', { replace: true })
            return
          }
        } else {
          toast.success(res?.data?.message || 'Homework updated successfully.')
        }
      } else {
        const payload = {
          institute_id: instituteId,
          class_id: classId,
          title: form.title.trim(),
          due_date: form.due_date,
          status: form.status || 'published',
        }

        if (Number.isFinite(subjectId) && subjectId > 0) {
          payload.subject_id = subjectId
        }
        if (description) payload.description = description
        if (sessionId) payload.academic_session_id = sessionId

        const isAdmin =
          normalizedUserType === 'admin' ||
          normalizedRoles.includes('admin') ||
          normalizedRoles.includes('superadmin')
        if (isAdmin) {
          const teacherId = Number(user?.teacher_id ?? user?.id)
          if (Number.isFinite(teacherId) && teacherId > 0) {
            payload.teacher_id = teacherId
          }
        }

        const res = await createHomeworkRequest(payload)
        const homeworkId = parseHomeworkIdFromResponse(res)

        if (mediaFile) {
          if (!homeworkId) {
            toast.error(
              'Homework was saved, but the attachment could not be uploaded (missing homework id).',
            )
            navigate('/homework', { replace: true })
            return
          }
          try {
            const uploadResult = await uploadHomeworkMedia(homeworkId, mediaFile)
            toast.success(
              uploadResult.message ||
                res?.data?.message ||
                'Homework created with attachment.',
            )
          } catch (uploadErr) {
            toast.error(
              `${getHomeworkErrorMessage(uploadErr)} Homework was saved without the attachment.`,
            )
            navigate('/homework', { replace: true })
            return
          }
        } else {
          toast.success(res?.data?.message || 'Homework created.')
        }
      }

      navigate('/homework', { replace: true })
    } catch (err) {
      toast.error(getHomeworkErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  if (!canAccessHomework) {
    return <Navigate to="/Dashboard" replace />
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .form-control, .form-select {
            border-radius: 10px !important;
            border: 1px solid #D1D3D4 !important;
            padding: 10px 12px !important;
            font-family: 'Inter', system-ui, sans-serif;
          }
          @media (max-width: 767.98px) {
            .homework-form-actions {
              flex-wrap: nowrap !important;
            }
            .homework-form-actions .btn {
              flex: 1 1 0;
              min-width: 0 !important;
            }
          }
          .form-label {
            font-weight: 600;
            color: #111827;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .selectable-field-label {
            font-size: 13px !important;
            font-weight: 600;
            margin-bottom: 6px;
            color: #475569;
          }
          .selectable-field-typeahead .rbt-input-main {
            height: 36px !important;
            font-size: 13px !important;
            padding: 6px 12px !important;
            border-radius: 0px !important;
            background-color: white !important;
          }
          .selectable-field-typeahead .rbt-input-main:focus {
            box-shadow: 0 0 0 3px rgba(255, 178, 178, 0.15) !important;
            outline: none !important;
          }
          .selectable-field-typeahead .rbt-input-hint {
            height: 36px !important;
            font-size: 13px !important;
            padding: 6px 12px !important;
          }
          .selectable-field-typeahead .rbt-menu {
            font-size: 13px !important;
            max-height: 200px !important;
          }
          .selectable-field-typeahead .rbt-menu .dropdown-item {
            padding: 8px 12px !important;
            font-size: 13px !important;
          }
          .homework-card-header {
            min-height: 72px;
            display: flex;
            align-items: center;
            box-sizing: border-box;
          }
          .homework-card-col {
            display: flex;
          }
          .homework-card-col .card {
            width: 100%;
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px',
              }}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title text-start"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2',
                    }}
                  >
                    {isEditMode ? 'Update Homework' : 'Create Homework'}
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}
                  >
                    {isEditMode
                      ? 'Edit homework details and save your changes.'
                      : 'Fill in details and assign homework to your class.'}
                  </p>
                </div>
              </div>

              {!isMobile && (
                <ProfileDropdown logout={logout} notificationCount={3} showNotifications={true} />
              )}
            </div>
          </div>
        </div>

        <Container
          style={{
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '1400px',
          }}
        >
          {loadingEdit ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                Loading homework…
              </span>
            </div>
          ) : (
          <>
          <Row className="g-2 mb-4">
            <Col xs={12} md={6}>
              <Form.Group className="mb-0 text-start">
                <Form.Label className="selectable-field-label">Select Class</Form.Label>
                <div className="selectable-field-typeahead">
                  <Typeahead
                    id="homework-class-select"
                    labelKey={(cls) => formatClassWithCourse(cls, courses)}
                    isLoading={filtersLoading || loadingEdit}
                    onChange={(selected) => {
                      const cls = selected[0]
                      const classId = cls ? String(resolveClassId(cls)) : ''
                      setSelectedClassId(classId)
                      setSelectedSubjectId('')
                    }}
                    options={assignedClasses}
                    placeholder={
                      filtersLoading
                        ? 'Loading classes…'
                        : assignedClasses.length
                          ? 'Select Class'
                          : 'No assigned classes'
                    }
                    emptyLabel="No assigned classes found"
                    selected={assignedClasses.filter(
                      (cls) => String(resolveClassId(cls)) === String(selectedClassId),
                    )}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-0 text-start">
                <Form.Label className="selectable-field-label">Select Subject (optional)</Form.Label>
                <div className="selectable-field-typeahead">
                  <Typeahead
                    id="homework-subject-select"
                    labelKey="subject_name"
                    clearButton
                    isLoading={(filtersLoading || loadingEdit) && !!selectedClassId}
                    onChange={(selected) => {
                      const subject = selected[0]
                      setSelectedSubjectId(subject ? String(subject.id) : '')
                    }}
                    options={classSubjects}
                    placeholder={
                      !selectedClassId
                        ? 'Select class first'
                        : classSubjects.length
                          ? 'Select Subject (optional)'
                          : 'No subjects for this class'
                    }
                    emptyLabel="No subjects found for this class"
                    disabled={!selectedClassId}
                    selected={
                      selectedSubjectId
                        ? classSubjects.filter(
                            (s) => String(s.id) === String(selectedSubjectId),
                          )
                        : []
                    }
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Form id="homework-form" onSubmit={handleSubmit}>
            <Row className="g-4">
              <Col xs={12} md={6} className="homework-card-col">
                <Card
                  className="h-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className="homework-card-header"
                    style={{
                      background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                      padding: '16px 24px',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3 w-100 flex-wrap">
                      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <JournalBookmark size={18} style={{ color: '#ffffff' }} />
                        </div>
                        <h5
                          className="m-0 text-white"
                          style={{
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          Description
                        </h5>
                      </div>
                      <Badge
                        bg="light"
                        text="primary"
                        className="ms-auto"
                        style={{ border: '1px solid rgba(255,255,255,0.4)' }}
                      >
                        {isEditMode ? 'Edit' : 'New'}
                      </Badge>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label>Title *</Form.Label>
                      <Form.Control
                        value={form.title}
                        onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. Mathematics — Chapter 5"
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label>Homework description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={form.description}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, description: e.target.value }))
                        }
                        placeholder="Write the homework instructions for students..."
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6} className="homework-card-col">
                <Card
                  className="h-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className="homework-card-header"
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      padding: '16px 24px',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 w-100">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Paperclip size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        className="m-0 text-white"
                        style={{ fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        Media upload
                      </h5>
                    </div>
                  </div>
                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <MediaUploadField
                      label="Attachment (optional)"
                      file={mediaFile}
                      fileLabel={mediaMeta.label}
                      previewUrl={mediaMeta.previewUrl}
                      onFileChange={handleMediaFileChange}
                      onClear={clearMedia}
                      disabled={submitting || loadingEdit}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>

          <Row className="mt-4 g-3">
            <Col xs={12} md={6}>
              <Form.Group className="mb-0 text-start">
                <Form.Label>Due date *</Form.Label>
                <Form.Control
                  form="homework-form"
                  type="date"
                  value={form.due_date}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, due_date: e.target.value }))
                  }
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-0 text-start">
                <Form.Label>Status *</Form.Label>
                <Form.Select
                  form="homework-form"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  required
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div
            className="d-flex justify-content-end gap-3 mt-4 pt-3 flex-wrap homework-form-actions"
            style={{ borderTop: '1px solid #E5E7EB' }}
          >
            <Button
              type="submit"
              form="homework-form"
              disabled={submitting || loadingEdit}
              style={{
                backgroundColor: '#10B981',
                border: 'none',
                color: '#ffffff',
                borderRadius: '8px',
                padding: '12px 24px',
                fontWeight: '600',
                minWidth: '160px',
              }}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : isEditMode ? (
                'Update homework'
              ) : (
                'Create homework'
              )}
            </Button>
            <Button
              type="button"
              variant="light"
              className="border"
              disabled={submitting}
              onClick={() => navigate('/homework')}
              style={{
                borderRadius: '8px',
                padding: '12px 24px',
                fontWeight: '600',
                minWidth: '140px',
              }}
            >
              Cancel
            </Button>
          </div>
          </>
          )}
        </Container>
      </div>
    </>
  )
}
